import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck, PlaneTakeoff, PlaneLanding, ArrowRight, CalendarClock,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, bookingPill, withAlpha } from "../lib/bookingStatus";
import { deDateMediumShortYear } from "../lib/dateFormat";

/**
 * Feb 2026 · User-Directive „drei Kacheln oben auf der Concierge-Home".
 *
 * Drei-Kachel-Zeile:
 *   • Meine offenen Aufgaben  → Zähler (open_bookings)
 *   • Meine nächste Abreise   → früheste Buchung mit from_date ≥ heute
 *   • Meine nächste Anreise   → früheste Buchung mit to_date  ≥ heute
 *
 * Quelle: `/api/team/concierge-stats`. Für die zwei Zeit-Kacheln liefert
 * der Endpoint direkt `next_departure` / `next_arrival` als angereichertes
 * Objekt (booking_id + Datum + Kunde + Objekt), damit die Kachel ohne
 * separaten Lookup rendern kann.
 *
 * Klick-Ziele:
 *   • Aufgaben  → `/team/bookings` (Liste)
 *   • Abreise   → `/team/bookings?open=<booking_id>` (Detail-Modal auto)
 *   • Anreise   → `/team/bookings?open=<booking_id>` (Detail-Modal auto)
 *
 * Die frühere „Meine Projekte"-Kachel ist jetzt ein eigener voll-breiter
 * Balken direkt darunter (siehe `ConciergeProjectsBar`).
 */
export default function ConciergeCockpitChips() {
  const { user, activeConciergeId } = useAuth();
  const [row, setRow] = useState(null);
  const [error, setError] = useState(false);

  const scopeId = activeConciergeId || (user?.role === "concierge" ? user.id : null);

  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/team/concierge-stats");
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const mine = list.find((r) => r?.concierge?.id === scopeId) || list[0] || null;
        setRow(mine);
      } catch (e) {
        if (!cancelled) { setError(true); setRow(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [scopeId]);

  if (!scopeId) return null;
  const isLoading = row === null && !error;
  // Feb 2026 · User-Directive „Meine offenen Aufgaben zeigt alles
  // Zugewiesene": `open_items_count` = echte Buchungen + offene Service-
  // Requests aller Sources (request_anything, team_captured, extras_bulk,
  // booking_change). Fallback auf `open_bookings` für ältere Backends.
  const openTasksCount = row?.open_items_count ?? row?.open_bookings ?? 0;
  const nextDep = row?.next_departure || null;
  const nextArr = row?.next_arrival || null;

  return (
    <div
      className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3"
      data-testid="concierge-cockpit-chips"
      data-scope={scopeId}
      data-source="concierge-stats"
    >
      <CountChip
        to="/team/bookings"
        icon={ClipboardCheck}
        label="Meine offenen Aufgaben"
        count={openTasksCount}
        loading={isLoading}
        accent={STATUS_COLORS.blue}
        testid="cockpit-chip-tasks"
        emptyLabel="Alles erledigt"
      />
      <BookingChip
        icon={PlaneTakeoff}
        label="Meine nächste Abreise"
        booking={nextDep}
        dateField="from_date"
        loading={isLoading}
        fallbackAccent={BRAND_COLORS.gold}
        testid="cockpit-chip-departure"
        emptyLabel="Keine geplante Abreise"
      />
      <BookingChip
        icon={PlaneLanding}
        label="Meine nächste Anreise"
        booking={nextArr}
        dateField="to_date"
        loading={isLoading}
        fallbackAccent={STATUS_COLORS.green}
        testid="cockpit-chip-arrival"
        emptyLabel="Keine geplante Anreise"
      />
    </div>
  );
}

function CountChip({ to, icon: Icon, label, count, loading, accent, testid, emptyLabel }) {
  const isZero = !loading && count === 0;
  const numberColor = isZero ? BRAND_COLORS.navy : accent;
  return (
    <Link
      to={to}
      data-testid={testid}
      className="bg-white border border-navy/10 hover:border-gold transition-colors group flex items-center gap-4 px-5 py-4"
    >
      <div
        className="w-11 h-11 shrink-0 flex items-center justify-center"
        style={{
          background: withAlpha(accent, isZero ? 0.05 : 0.1),
          border: `1px solid ${withAlpha(accent, isZero ? 0.15 : 0.25)}`,
        }}
      >
        <Icon size={20} strokeWidth={1.5} style={{ color: numberColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <span
          className="font-display text-3xl md:text-4xl leading-none tabular-nums"
          style={{ color: numberColor }}
          data-testid={`${testid}-count`}
        >
          {loading ? "…" : count}
        </span>
        <div className="text-[12.5px] text-navy-dark/70 mt-1 leading-tight">
          {label}
        </div>
        {isZero && (
          <div className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/45 mt-0.5">
            {emptyLabel}
          </div>
        )}
      </div>
      <ArrowRight
        size={14}
        strokeWidth={1.8}
        className="shrink-0 text-navy-dark/30 group-hover:text-gold transition-colors"
      />
    </Link>
  );
}

// Feb 2026 · Deutsche Datumsformatierung ohne externe Deps — `Intl` kann
// unser ISO-Datum direkt konsumieren, ist stabil in allen Browsern.
function formatDe(iso) {
  if (!iso) return "—";
  try {
    return deDateMediumShortYear(iso);
  } catch {
    return iso;
  }
}

function BookingChip({ icon: Icon, label, booking, dateField, loading, fallbackAccent, testid, emptyLabel }) {
  const hasBooking = !!booking && !!booking?.booking_id;
  // Feb 2026 · User-Directive „Kachel-Farbe folgt Buchungsstatus".
  // Solange Buchung + Statusfelder vorhanden sind, leiten wir die
  // Kachel-Farbe aus derselben 4-Farb-Pille ab, die auch das
  // Detail-Modal + Team-Kalender benutzen (`bookingPill`). Fallback-
  // Accent (Gold für Abreise, Grün für Anreise) greift nur im Empty-
  // State, damit die Empty-Kachel visuell nicht zu blass wird.
  const pill = hasBooking ? bookingPill(booking) : null;
  const accent = pill?.bg || fallbackAccent;
  const numberColor = hasBooking ? accent : BRAND_COLORS.navy;
  const dateStr = hasBooking ? formatDe(booking[dateField]) : null;
  const to = hasBooking
    ? `/team/bookings?open=${encodeURIComponent(booking.booking_id)}`
    : "/team/bookings";
  return (
    <Link
      to={to}
      data-testid={testid}
      data-booking-id={hasBooking ? booking.booking_id : ""}
      data-status={pill?.label || ""}
      data-accent={accent}
      className="relative bg-white border border-navy/10 hover:border-gold transition-colors group flex items-center gap-4 px-5 py-4 pr-14"
    >
      {/* Feb 2026 · User-Directive „Status-Pille auf Kachel". Kleine
          BOOKING_PILL rechts oben — Concierge liest Farbe + Klartext
          („Erledigt", „In Betreuung", …) auf einen Blick, ohne das
          Detail-Modal zu öffnen. `pointer-events-none`, damit der Klick
          weiterhin die ganze Kachel + den `<Link>` triggert. Nur bei
          vorhandener Buchung sichtbar; Empty-State bleibt aufgeräumt. */}
      {pill && (
        <span
          className="absolute top-2.5 right-3 pointer-events-none px-2 py-0.5 text-[9px] tracking-[0.16em] uppercase font-medium whitespace-nowrap rounded-full"
          style={{ background: pill.bg, color: pill.fg }}
          data-testid={`${testid}-pill`}
          data-pill-label={pill.label}
        >
          {pill.label}
        </span>
      )}
      <div
        className="w-11 h-11 shrink-0 flex items-center justify-center"
        style={{
          background: withAlpha(accent, hasBooking ? 0.1 : 0.05),
          border: `1px solid ${withAlpha(accent, hasBooking ? 0.25 : 0.15)}`,
        }}
      >
        <Icon size={20} strokeWidth={1.5} style={{ color: numberColor }} />
      </div>
      <div className="flex-1 min-w-0">
        {loading ? (
          <span
            className="font-display text-2xl leading-none tabular-nums text-navy-dark/40"
            data-testid={`${testid}-date`}
          >
            …
          </span>
        ) : hasBooking ? (
          <span
            className="font-display text-2xl md:text-[26px] leading-none tabular-nums"
            style={{ color: numberColor }}
            data-testid={`${testid}-date`}
          >
            {dateStr}
          </span>
        ) : (
          <span
            className="font-display text-xl leading-none text-navy-dark/50"
            data-testid={`${testid}-empty`}
          >
            —
          </span>
        )}
        <div className="text-[12.5px] text-navy-dark/70 mt-1 leading-tight">
          {label}
        </div>
        {hasBooking ? (
          <div className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/55 mt-0.5 truncate">
            {[booking.customer_name, booking.property_name].filter(Boolean).join(" · ") || "—"}
          </div>
        ) : (
          <div className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/45 mt-0.5">
            {emptyLabel}
          </div>
        )}
      </div>
      <ArrowRight
        size={14}
        strokeWidth={1.8}
        className="absolute right-4 bottom-4 text-navy-dark/30 group-hover:text-gold transition-colors"
      />
    </Link>
  );
}
